import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import FaIcon from "../utils/fa_icon";
import AppTile from "../header/app-switch/app-tile";
import { productList } from "../header/menu-list";
import { isSettingsPath } from "../header/settings-menu";
import SidebarTooltip from "./sidebar_tooltip";
import styles from "./sidemenu.module.scss";

const RETURN_PATH_KEY = "art.console.settings_return_to";


export function useTrackSettingsReturnPath() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (isSettingsPath(pathname)) return;
    try {
      sessionStorage.setItem(RETURN_PATH_KEY, `${pathname}${search}`);
    } catch {
      // Storage unavailable — the back link falls back to home.
    }
  }, [pathname, search]);
}

function readReturnPath(): string | null {
  try {
    return sessionStorage.getItem(RETURN_PATH_KEY);
  } catch {
    return null;
  }
}

export default function SettingsBackLink({ isToggled }: { isToggled: boolean }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  if (!isSettingsPath(pathname)) return null;

  const returnPath = readReturnPath();
  const app = returnPath
    ? productList.find(
        ({ prefix }) =>
          returnPath === `/${prefix}` || returnPath.startsWith(`/${prefix}/`),
      )
    : undefined;
  const destination = app?.title ?? "Home";
  const label = `Back to ${destination}`;

  return (
    <div className={styles.settings_back}>
      <SidebarTooltip
        title={label}
        placement="right"
        disableHoverListener={!isToggled}
      >
        <button
          type="button"
          className={classNames(styles.settings_back_link, {
            [styles.toggled]: isToggled,
          })}
          style={app ? { ["--accent" as string]: app.accent } : undefined}
          aria-label={label}
          data-qa="sidebar-settings-back"
          onClick={() => navigate(app && returnPath ? returnPath : "/")}
        >
          {!isToggled && (
            <FaIcon icon={faArrowLeft} className={styles.settings_back_arrow} />
          )}
          {app ? (
            <AppTile app={app} className={styles.settings_back_tile} />
          ) : (
            isToggled && (
              <FaIcon icon={faArrowLeft} className={styles.settings_back_arrow} />
            )
          )}
          {!isToggled && (
            <span className={styles.settings_back_text}>
              <span className={styles.settings_back_caption}>Back to</span>
              <span className={styles.settings_back_title}>{destination}</span>
            </span>
          )}
        </button>
      </SidebarTooltip>
    </div>
  );
}
